import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useUserData } from '../context/UserDataContext';

// ── Bucket map (mirrors Budget.js RULE_BUCKETS) ────────────────────────────
const RULE_BUCKETS = {
  'Housing': 'needs', 'Utilities': 'needs', 'Groceries': 'needs',
  'Transportation': 'needs', 'Insurance': 'needs', 'Healthcare': 'needs',
  'Minimum Debt Payments': 'needs',
  'Dining Out': 'wants', 'Entertainment': 'wants', 'Shopping': 'wants',
  'Subscriptions': 'wants', 'Personal Care': 'wants', 'Travel': 'wants',
  'Fitness': 'wants', 'Hobbies': 'wants',
  'Emergency Fund': 'savings', 'Retirement': 'savings', 'Investments': 'savings',
  'Extra Debt Paydown': 'savings', 'Education Fund': 'savings',
};

const INVESTMENT_TYPES = ['brokerage', '401k', 'roth_ira', 'ira', 'crypto', 'real_estate', 'other'];

// ── Score calculators (all return null when data is absent) ──────────────────

function computeBudgetScore(budgetData) {
  const income = budgetData?.income || 0;
  if (income <= 0) return null;

  const categories = budgetData?.categories || [];
  const transactions = budgetData?.transactions || [];

  // Compute bucket spending from transactions if available, else from categories
  const buckets = { needs: 0, wants: 0, savings: 0 };
  if (transactions.length > 0) {
    const now = new Date();
    const mStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const mEnd   = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    transactions
      .filter(t => {
        const d = new Date(t.date + 'T12:00:00');
        return t.kind === 'expense' && d >= mStart && d <= mEnd;
      })
      .forEach(t => {
        let bucket;
        if (t.type === 'Need')         bucket = 'needs';
        else if (t.type === 'Want')    bucket = 'wants';
        else if (t.type === 'Savings') bucket = 'savings';
        else if (t.category)           bucket = RULE_BUCKETS[t.category];
        if (bucket) buckets[bucket] += t.amount;
      });
  } else {
    categories.forEach(c => {
      const bucket = c.type === 'Need' ? 'needs'
        : c.type === 'Want' ? 'wants'
        : c.type === 'Savings' ? 'savings'
        : RULE_BUCKETS[c.name];
      if (bucket) buckets[bucket] += (c.spent || 0);
    });
  }

  const totalBucketed = buckets.needs + buckets.wants + buckets.savings;
  if (totalBucketed === 0) return null;

  const needsPct    = (buckets.needs    / income) * 100;
  const wantsPct    = (buckets.wants    / income) * 100;
  const savingsPct  = (buckets.savings  / income) * 100;

  // Penalty: excess over target (needs > 50, wants > 30) or shortfall under savings target (savings < 20)
  const penalty =
    Math.max(0, needsPct   - 50) * 0.1 +
    Math.max(0, wantsPct   - 30) * 0.2 +
    Math.max(0, 20 - savingsPct) * 0.2;

  return Math.max(0, Math.min(10, 10 - penalty));
}

function computeSavingsScore(budgetData) {
  const income = budgetData?.income || 0;
  if (income <= 0) return null;

  const transactions = budgetData?.transactions || [];
  const categories   = budgetData?.categories   || [];

  let totalSpent = 0;
  if (transactions.length > 0) {
    const now = new Date();
    const mStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const mEnd   = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    totalSpent = transactions
      .filter(t => { const d = new Date(t.date + 'T12:00:00'); return t.kind === 'expense' && d >= mStart && d <= mEnd; })
      .reduce((s, t) => s + t.amount, 0);
  } else {
    totalSpent = categories.reduce((s, c) => s + (c.spent || 0), 0);
  }

  const saved = income - totalSpent;
  const rate  = (saved / income) * 100;
  if (rate <= 0) return 0;
  return Math.min(10, (rate / 20) * 10);
}

function computeRetirementScore(retirementData) {
  const rate = retirementData?.successRate;
  if (!rate || rate <= 0) return null;
  if (rate >= 90) return 10;
  if (rate >= 70) return 5 + ((rate - 70) / 20) * 5;
  if (rate >= 50) return (rate - 50) / 20 * 5;
  return 0;
}

function computeDebtScore(userProfile, budgetData) {
  const debts = userProfile?.debts;
  if (!debts || debts.length === 0) return null;

  const annualIncome = (budgetData?.income || 0) * 12;
  if (annualIncome <= 0) return null;

  const totalDebt = debts.reduce((s, d) => s + (parseFloat(d.balance) || 0), 0);
  const dti = (totalDebt / annualIncome) * 100; // debt-to-income ratio (%)

  // Score 10 at DTI ≤ 20%, 0 at DTI ≥ 60%
  if (dti <= 20) return 10;
  if (dti >= 60) return 0;
  return 10 - ((dti - 20) / 40) * 10;
}

function computeDiversificationScore(linkedAccountsData) {
  const investmentAccounts = (linkedAccountsData || []).filter(a => INVESTMENT_TYPES.includes(a.type));
  const allHoldings = investmentAccounts.flatMap(a => a.holdings || []);
  if (allHoldings.length === 0) return null;

  const assetClasses = new Set(allHoldings.map(h => h.assetClass).filter(Boolean));
  const count = assetClasses.size;
  if (count >= 3) return 10;
  if (count === 2) return 6;
  if (count === 1) return 3;
  return null;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function ScoreBar({ score }) {
  const color = score >= 7 ? '#34d399' : score >= 4 ? '#fbbf24' : '#f87171';
  const width  = `${(score / 10) * 100}%`;
  return (
    <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-700"
        style={{ width, backgroundColor: color }}
      />
    </div>
  );
}

function ScoreRow({ label, score, hint, onNavigate, navTab }) {
  const isNull  = score === null;
  const display = isNull ? '—' : score.toFixed(1);
  const color   = isNull ? 'text-slate-500'
    : score >= 7 ? 'text-emerald-400'
    : score >= 4 ? 'text-amber-400'
    : 'text-rose-400';

  return (
    <div className="flex items-center gap-3">
      <div className="w-36 flex-shrink-0 text-xs text-slate-400 truncate">{label}</div>
      {isNull ? (
        <div className="flex-1 flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-slate-700/50 rounded-full" />
          <span className="text-xs text-slate-500 italic truncate">{hint}</span>
          {navTab && onNavigate && (
            <button
              onClick={() => onNavigate(navTab)}
              className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-0.5 flex-shrink-0 transition-colors"
            >
              Add <ArrowRight size={10} />
            </button>
          )}
        </div>
      ) : (
        <>
          <ScoreBar score={score} />
          <div className={`text-sm font-bold tabular-nums w-8 text-right ${color}`}>{display}</div>
        </>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function FinancialHealthScorecard({ setActiveTab }) {
  const { budgetData, retirementData, userProfile, linkedAccountsData } = useUserData();

  const categories = [
    {
      label:  'Budget Health',
      score:  computeBudgetScore(budgetData),
      hint:   'Set up budget',
      navTab: 'budget',
    },
    {
      label:  'Savings Rate',
      score:  computeSavingsScore(budgetData),
      hint:   'Set up budget',
      navTab: 'budget',
    },
    {
      label:  'Retirement',
      score:  computeRetirementScore(retirementData),
      hint:   'Run planner',
      navTab: 'retirement',
    },
    {
      label:  'Debt Load',
      score:  computeDebtScore(userProfile, budgetData),
      hint:   'Add debts',
      navTab: 'debt',
    },
    {
      label:  'Diversification',
      score:  computeDiversificationScore(linkedAccountsData),
      hint:   'Add holdings',
      navTab: 'investing',
    },
  ];

  const available = categories.filter(c => c.score !== null);
  const overall   = available.length > 0
    ? available.reduce((s, c) => s + c.score, 0) / available.length
    : null;

  const overallColor = overall === null ? 'text-slate-500'
    : overall >= 7 ? 'text-emerald-400'
    : overall >= 4 ? 'text-amber-400'
    : 'text-rose-400';

  const overallLabel = overall === null ? 'No data yet'
    : overall >= 8 ? 'Excellent'
    : overall >= 6 ? 'Good'
    : overall >= 4 ? 'Fair'
    : 'Needs attention';

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h2 className="font-semibold text-slate-200 text-base">Financial Health</h2>
          <p className="text-xs text-slate-500 mt-0.5">Based on your live data across all modules</p>
        </div>
        <div className="text-right flex-shrink-0">
          <div className={`text-3xl font-bold tabular-nums leading-none ${overallColor}`}>
            {overall !== null ? overall.toFixed(1) : '—'}
          </div>
          <div className="text-xs text-slate-500 mt-1">{overallLabel}</div>
        </div>
      </div>

      <div className="space-y-3">
        {categories.map(c => (
          <ScoreRow
            key={c.label}
            label={c.label}
            score={c.score}
            hint={c.hint}
            navTab={c.navTab}
            onNavigate={setActiveTab}
          />
        ))}
      </div>

      {available.length < categories.length && (
        <p className="text-xs text-slate-600 mt-4 pt-3 border-t border-slate-700/60">
          Score improves as you add data to each module ({categories.length - available.length} incomplete)
        </p>
      )}
    </div>
  );
}
